/** One value group of the detail panel's grouped view: a SectionCard titled
 *  with the shared value preview, whose body is a chip per tenant occurrence
 *  linking to that operator's Configuration tab. For `[*]` keys a tenant
 *  appears once per concrete path (the chip tooltip disambiguates). */
import { Box, Button, Chip, Tooltip } from '@mui/material';
import { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { SectionCard } from '@/components/ReadView';
import { ValueActions } from '@/components/ValueActions/ValueActions';

import { batchTenantActions } from '../tenantLinks';

export interface ValueGroupItem {
  /** Unique per chip — `${tenantId}:${concretePath}`. */
  key: string;
  tenantId: number;
  name: string;
  /** The operator code, for the chip's copy action. */
  code?: string | null;
  /** Concrete path shown on hover for `[*]` keys. */
  tooltip?: string;
  /** Value comes from a pending (unsaved) edit. */
  pending?: boolean;
}

export function ValueGroupSection({
  title,
  items,
  collapsedByDefault = false,
}: {
  title: string;
  items: ValueGroupItem[];
  /** Large "(unset)" groups start behind a "Show N" toggle. */
  collapsedByDefault?: boolean;
}) {
  const [expanded, setExpanded] = useState(!collapsedByDefault);

  return (
    <SectionCard title={title}>
      {expanded ? (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {items.map((item) => {
            // The chip keeps its own click (to that operator's Configuration
            // tab, same tab); the popover only adds the new-tab open and copy.
            const chip = (
              <ValueActions
                key={item.key}
                chip
                actions={batchTenantActions({ id: item.tenantId, code: item.code })}
              >
                <Chip
                  label={item.name}
                  size="small"
                  clickable
                  component={RouterLink}
                  to={`/tour-operators/${item.tenantId}/configuration`}
                  {...(item.pending ? { color: 'warning' as const, variant: 'outlined' as const } : {})}
                />
              </ValueActions>
            );
            return item.tooltip ? (
              <Tooltip key={item.key} title={item.tooltip}>
                {chip}
              </Tooltip>
            ) : (
              chip
            );
          })}
        </Box>
      ) : (
        <Button size="small" onClick={() => setExpanded(true)}>
          Show {items.length} unset
        </Button>
      )}
    </SectionCard>
  );
}
